import { Text, View } from "@react-pdf/renderer";
import { PDFHtmlTags, PDFStyleConfigs } from "../../configs";
import { isNil } from "lodash";
import { useTheme } from "@mui/material";
import React from "react";
import { useAnalysisPDFStyles } from "@hooks/useAnalysisPDFStyle";

export const PDFRichText = ({
  htmlString,
  fontSize,
}: {
  htmlString: string | null;
  fontSize: number;
}) => {
  const { typographyStyle } = useAnalysisPDFStyles();
  const { palette } = useTheme();

  const convertHtmlToPdf = (htmlString: string): JSX.Element[] => {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(htmlString, "text/html");

    return Array.from(parsedHtml.body.childNodes).map((node, index) => (
      <React.Fragment key={index}>{convertNodeToPdf(node)}</React.Fragment>
    ));
  };

  const convertNodeToPdf = (node: ChildNode): JSX.Element => {
    if (node.nodeType === Node.TEXT_NODE) {
      return <Text>{node.textContent}</Text>;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      switch (tagName) {
        case PDFHtmlTags.P:
          return (
            <Text
              style={{
                ...typographyStyle.body1,
                color: palette.common.black,
                textAlign: "left",
                lineHeight: 1.2,
                fontSize,
              }}
            >
              {Array.from(element.childNodes).map((childNode, index) => (
                <React.Fragment key={index}>
                  {convertNodeToPdf(childNode)}
                </React.Fragment>
              ))}
            </Text>
          );
        case PDFHtmlTags.STRONG:
          return (
            <Text style={{ fontWeight: "bold" }}>
              {Array.from(element.childNodes).map((childNode, index) => (
                <React.Fragment key={index}>
                  {convertNodeToPdf(childNode)}
                </React.Fragment>
              ))}
            </Text>
          );
        case PDFHtmlTags.EM:
        case PDFHtmlTags.I:
          return (
            <Text style={{ fontStyle: "italic" }}>
              {Array.from(element.childNodes).map((childNode, index) => {
                return (
                  <React.Fragment key={index}>
                    {convertNodeToPdf(childNode)}
                  </React.Fragment>
                );
              })}
            </Text>
          );
        case PDFHtmlTags.U:
          return (
            <Text style={{ textDecoration: "underline" }}>
              {Array.from(element.childNodes).map((childNode, index) => {
                return (
                  <React.Fragment key={index}>
                    {convertNodeToPdf(childNode)}
                  </React.Fragment>
                );
              })}
            </Text>
          );
        case PDFHtmlTags.OL:
          return (
            <View style={{ marginLeft: 15 }}>
              {Array.from(element.children).map((childNode, index) => (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 5,
                  }}
                  key={index}
                >
                  <Text
                    style={{
                      ...typographyStyle.body1,
                      color: palette.common.black,
                      textAlign: "justify",
                      lineHeight: 1.2,
                      fontSize,
                    }}
                  >
                    {`${index + 1}. `}
                    {Array.from(childNode.childNodes).map(
                      (subChildNode, index) => {
                        return (
                          <React.Fragment key={index}>
                            {convertNodeToPdf(subChildNode)}
                          </React.Fragment>
                        );
                      },
                    )}
                  </Text>
                </View>
              ))}
            </View>
          );
        case PDFHtmlTags.UL:
          return (
            <View style={{ marginLeft: 15 }}>
              {Array.from(element.children).map((childNode, index) => (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 5,
                  }}
                  key={index}
                >
                  <Text
                    style={{
                      ...typographyStyle.body1,
                      color: palette.common.black,
                      textAlign: "justify",
                      lineHeight: 1.2,
                      fontSize,
                    }}
                  >
                    •{" "}
                    {Array.from(childNode.childNodes).map(
                      (subChildNode, index) => (
                        <React.Fragment key={index}>
                          {convertNodeToPdf(subChildNode)}
                        </React.Fragment>
                      ),
                    )}
                  </Text>
                </View>
              ))}
            </View>
          );
        case PDFHtmlTags.BR:
          return <Text style={{ gap: PDFStyleConfigs.GapXSmall }}>{`\n`}</Text>;
        case PDFHtmlTags.SPAN:
          return <Text>{node?.textContent}</Text>;
        default:
          return <></>;
      }
    }

    return <></>;
  };

  return <View>{!isNil(htmlString) && convertHtmlToPdf(htmlString)}</View>;
};
